/**
 * The IndexedDB plumbing, and the only file that touches `indexedDB` directly.
 *
 * Hand-written rather than installed, for the same reason `dom.ts`, `router.ts`
 * and `http.ts` are: the surface actually needed here is a handful of calls, and
 * trayflux ships no runtime dependencies.
 *
 * **One database per user**, named for them. Not one database with a `user_id`
 * on every row — that puts "remember to filter" back into every read, which is
 * exactly what `api/src/repo/lists.ts` refuses to do on the server. It also
 * makes sign-out a single `deleteDatabase` with nothing to scan and nothing to
 * miss. A shared list (g71) gets one of its own for the same reason: per list,
 * link, generation and visitor (`scopes.ts`).
 *
 * The one rule every caller must keep: **a transaction commits as soon as its
 * requests drain and control returns to the event loop.** Never `await`
 * something that is not an IndexedDB request between two requests of one
 * transaction. Issue follow-up requests from inside a request's `onsuccess`,
 * and `await committed(tx)` at the end.
 */

import { SHARED_PREFIX, scopeDatabaseName, type StoreScope } from "./scopes.js";

/**
 * 2: `position` (a float) became `orderKey` (a string) on both stores.
 *
 * Held rows are not translated, they are dropped -- see `onupgradeneeded`. A
 * row carrying the old field would sort against `undefined`, which in a string
 * comparison is not an error, just the wrong order, and it would be wrong
 * quietly and forever.
 *
 * 3: add atomic-import provenance and recovery drafts. Preserve every v2 row
 * and queued mutation; only the earlier v1 ordering migration clears data.
 *
 * 4: add `orders`, what Auto-order (g62) needed to undo or roll back itself.
 * Only a store is added; every row and queued mutation is kept. Auto-order
 * went in g81 and nothing writes the store now. It stays, emptied at open,
 * because taking it away would take a version bump.
 *
 * g68's creators deliberately did not take a version: they live in `meta`,
 * under `creator:<id>`. A version bump cannot be undone, so an older build
 * rolled back to would find every mirror a newer one had opened unreadable
 * (`VersionError`) and could not start. Add a store only when that is worth it.
 */
export const DB_VERSION = 4;

export const LISTS = "lists";
export const ITEMS = "items";
export const META = "meta";
export const PENDING = "pending";
export const IMPORTS = "imports";
/** Unused since g81, which removed Auto-order. `retireLegacyOrders` empties it. */
export const ORDERS = "orders";

/** Items belonging to one list — the local half of the server's delete cascade. */
export const BY_LIST = "by-list";
/** Pending mutations for one entity — the coalescing lookup. Not unique. */
export const BY_KEY = "by-key";

/**
 * A user id is an account's scope, as it was before scopes existed (g71); a
 * shared scope is one list through one link for one visitor. See `scopes.ts`.
 */
export function databaseName(scope: string | StoreScope): string {
  return scopeDatabaseName(typeof scope === "string" ? { kind: "account", userId: scope } : scope);
}

export function openDatabase(scope: string | StoreScope): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName(scope), DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      const upgrading = event.oldVersion > 0 && event.oldVersion < 2;
      if (!db.objectStoreNames.contains(IMPORTS)) db.createObjectStore(IMPORTS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(ORDERS)) db.createObjectStore(ORDERS, { keyPath: "listId" });
      if (!db.objectStoreNames.contains(LISTS)) db.createObjectStore(LISTS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(ITEMS)) {
        const items = db.createObjectStore(ITEMS, { keyPath: "id" });
        items.createIndex(BY_LIST, "listId");
      }
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: "key" });
      if (!db.objectStoreNames.contains(PENDING)) {
        // `seq` is the queue's order, and autoIncrement is what keeps it
        // monotonic across reloads. Coalescing writes back under the same key,
        // so a later edit never reorders the mutation it merges into.
        const pending = db.createObjectStore(PENDING, { keyPath: "seq", autoIncrement: true });
        pending.createIndex(BY_KEY, "key");
        pending.createIndex(BY_LIST, "listId");
      }

      /**
       * An existing mirror is emptied rather than migrated, **and so is the
       * queue**, so the next sync is a full `since=0` download.
       *
       * Emptying the mirror is easy to justify: the server holds the same rows
       * and re-fetching them costs one request, which is cheaper and far harder
       * to get subtly wrong than translating every row in place.
       *
       * The queue is the harder call, because those are changes that have not
       * reached the server and this is their only copy. It goes anyway, because
       * it is the one place v1 shapes survive this upgrade: a pending record
       * holds a `before` snapshot of the row as it stood, and a v1 snapshot has
       * a `position` and no `orderKey`. Restoring one on a rejected mutation
       * puts a row with no key into the mirror, where it sorts against
       * `undefined` -- not a crash, just an order that is quietly wrong. Half
       * the alternatives are filters that have to enumerate every v1 shape
       * correctly; this enumerates none of them.
       *
       * That is a real cost and it is bounded to this one version step: v1
       * existed before anyone but its author used it.
       */
      if (!upgrading) return;
      const tx = request.transaction;
      if (!tx) return;
      for (const name of [LISTS, ITEMS, META, PENDING]) tx.objectStore(name).clear();
    };

    request.onsuccess = () => {
      const db = request.result;
      // Another tab is upgrading. Holding the old connection open would block it
      // forever; letting go costs this tab a reload and costs that one nothing.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () =>
      reject(request.error ?? new Error("Could not open the local database."));
    request.onblocked = () => reject(new Error("The local database is open in another tab."));
  });
}

/**
 * How long a delete may go unanswered before it is treated as blocked.
 *
 * An unhindered delete takes milliseconds. With another connection open,
 * Chrome under load sends `versionchange` and then neither `blocked` nor
 * `success`, so without a bound "Clearing this browser…" never ends (g49).
 */
export const DELETE_WAIT_MS = 4_000;

/** Another connection holds the database, or the browser never answered. The message is for the person. */
export class DeleteBlocked extends Error {
  constructor() {
    super("Close other Trayflux tabs and retry clearing this browser.");
  }
}

/**
 * A queued deletion is not a completed wipe. Let the caller explain how to
 * close a blocking tab and retry instead of claiming that the data is gone.
 *
 * Running out of time says the same: the request stays queued, and if it
 * completes later, the retry finds nothing left to delete and finishes.
 */
export function deleteDatabase(scope: string | StoreScope): Promise<void> {
  return deleteNamedDatabase(databaseName(scope));
}

/** By its name, for clearing a shared scope nothing holds open (g71). */
export function deleteNamedDatabase(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const blocked = () => new DeleteBlocked();
    const timer = setTimeout(() => reject(blocked()), DELETE_WAIT_MS);
    const settle = (outcome: () => void) => () => { clearTimeout(timer); outcome(); };
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = settle(resolve);
    request.onblocked = settle(() => reject(blocked()));
    request.onerror = settle(() =>
      reject(request.error ?? new Error("Could not clear the local data.")));
  });
}

/**
 * The shared scopes' databases this browser holds, where the browser can say.
 * Firefox before 126 cannot, and then only the registry knows.
 */
export async function sharedDatabaseNames(): Promise<string[]> {
  if (typeof indexedDB.databases !== "function") return [];
  const all = await indexedDB.databases().catch(() => []);
  return all.flatMap((entry) => entry.name?.startsWith(SHARED_PREFIX) ? [entry.name] : []);
}

export function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("The local database refused a read."));
  });
}

export function committed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("The local database refused a write."));
    tx.onabort = () => reject(tx.error ?? new Error("The local write was rolled back."));
  });
}

export function collect<T>(
  source: IDBObjectStore | IDBIndex,
  query?: IDBValidKey | IDBKeyRange,
): Promise<T[]> {
  return done(source.getAll(query) as IDBRequest<T[]>);
}
